import React, { useState } from 'react';
import { withStyles } from '@material-ui/core';
import { useTranslation } from "react-i18next";
import moment from "moment";

import { COLORS } from "../../../constant/colors";
import SectionHeader, { sectionHeaderSizes, sectionHeaderAlignDirections } from '../../components/core-elements/SectionHeader';
import FormTextField from '../../components/core-elements/FormTextField';
import DatePicker from '../../components/core-elements/DatePicker';
import Button from '../../components/core-elements/Button';
import Spacer from '../../components/core-elements/Spacer';
import Divider from '../../components/core-elements/Divider';
import AppIcon from '../../components/core-elements/AppIcon';
import ActionIcon from '../../components/core-elements/ActionIcon';
import ClickableText from '../../components/core-elements/ClickableText';
import tfIconSrc from '../../../assets/thoughtfull/TF_Trademark_Stacked.png';
import { momentDateToBUDateStringUtils, momentDateToYearString } from '../../../util/datetime';
import env from '../../../env';
import { getClientPolicy } from "../../../api/godp";
import { fontSizes } from '../../../constant/font';
import { triggerGAOnClickGetFreeTrialInValidationPopup, triggerGAOnSuccessfulValidation } from '../../../util/ga';

const PolicyValidationForm = ({ classes, onGoBack, onSubmitSuccess, onClickIAmNewCustomer, isFullPage = false }) => {
  const [idNumber, setIdNumber] = useState('');
  const [dob, setDob] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const onSubmit = async () => {
    setIsLoading(true);
    const momentDate = moment(dob);
    const dobStringBuFormat = momentDateToBUDateStringUtils[env.currentBu](momentDate);

    try {
      const res = await getClientPolicy(idNumber, dobStringBuFormat);
      const clientInfoObj = res;
      if (!clientInfoObj.hasPolicy || !clientInfoObj.clientId) { throw new Error('Invalid customer or missing valid policy'); }
      triggerGAOnSuccessfulValidation({ language: i18n.language, yearOfBirth: momentDateToYearString(momentDate) });
      onSubmitSuccess({ encryptedCustomerId: clientInfoObj.clientId, clientInfoObj, idNumber, dob });
      // Sample clientInfoObj:
      // {
      //   "code": 200,
      //   "hasPolicy": True,
      //   "email": email,
      //   "clientId": encrypted_client_id,
      //   "policy": first_matched_valid_client_product_obj.get('policy_id'),
      //   "issueDate": first_matched_valid_client_product_obj.get('effective_datetime'),
      //   "productCode": first_matched_valid_client_product_obj.get('product_code'),
      //   "Version": "Latest",
      // }
    } catch (err) {
      setErrorMessage(t("eTriageLanding.validation.errorMsg"));
      setIsLoading(false);
    }
  };

  const _onClickIAmNewCustomer = () => {
    triggerGAOnClickGetFreeTrialInValidationPopup({ language: i18n.language });
    onClickIAmNewCustomer();
  };

  return (
    <div className={classes.container}>
      {
        (!!onGoBack && !isFullPage) &&
        <div className={classes.backIconContainer}>
          <ActionIcon type='arrowLeft' onClick={onGoBack} />
        </div>
      }
      <SectionHeader
        text={t('eTriageLanding.validation.pleaseValidateYourPolicyFirst')}
        size={isFullPage ? sectionHeaderSizes.large : sectionHeaderSizes.medium}
        alignDirection={isFullPage ? sectionHeaderAlignDirections.center : sectionHeaderAlignDirections.left}
        onGoBack={!!isFullPage && onGoBack}
      />
      <div className={classes.formContainer}>
        <Spacer y={24} />
        <FormTextField title={t(`eTriageLanding.validation.${env.currentBu}.idDocument`)} value={idNumber} onValueChange={(value) => { setErrorMessage(''); setIdNumber(value); }} isError={!!errorMessage} />
        <Spacer y={16} />
        <DatePicker label={t("eTriageLanding.validation.dateOfBirth")} placeholder={'DD/MM/YYYY'} locale={i18n.language} value={dob} onChange={(value) => { setErrorMessage(''); setDob(value); }} isError={!!errorMessage} errorMessage={errorMessage} />
        <Spacer y={24} />
        <Button title={t("eTriageLanding.validation.go")} isDisabled={isLoading} onClick={onSubmit} />
        {
          !!onClickIAmNewCustomer &&
          <>
            <Spacer y={24} />
            <Divider />
            <Spacer y={24} />
            <div className={classes.newCustomerContainer}>
              <div><AppIcon imageSrc={tfIconSrc} /></div>
              <div className={classes.newCustomerTextContainer}>
                <div className={classes.newCustomerTitle}>{t("eTriageLanding.validation.newCustomer")}</div>
                <Spacer y={6} />
                <ClickableText text={t('eTriageLanding.validation.getAFreeTrail')} isShowArrow={true} onClick={_onClickIAmNewCustomer} />
              </div>
            </div>
          </>
        }
      </div>
    </div>
  )
};

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  backIconContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 16,
  },
  newCustomerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  newCustomerTextContainer: {
    paddingLeft: 16,
    flex: 1,
  },
  newCustomerTitle: {
    fontSize: fontSizes.contentMedium,
    fontWeight: 700,
    lineHeight: '20px',
    color: COLORS.fwdDarkGreen,
  },
  formContainer: {
    width: '100%',
    maxWidth: 344,
  },
};

export default withStyles(styles)(PolicyValidationForm);
